import type { RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { DEFAULT_COLUMN_WIDTH } from '../constants';
import type { ColumnWidthMap } from '../helpers';
import type { TableColumn } from '../types';

type FixedColumnOffsets = Record<string, number>;

interface MeasuredState {
  offsets: FixedColumnOffsets;
  totalLeft: number;
  totalRight: number;
}

interface UseFixedColumnsReturn {
  offsets: FixedColumnOffsets;
  totalLeftFixedWidth: number;
  totalRightFixedWidth: number;
}

/**
 * Hook to calculate fixed column offsets for sticky positioning.
 * Computes cumulative offsets for left-fixed and right-fixed columns.
 * Uses DOM measurement when ref provided for accurate widths.
 */
export const useFixedColumns = <T>(
  columns: TableColumn<T>[],
  widths: ColumnWidthMap,
  tableWrapperRef?: RefObject<HTMLDivElement | null>
): UseFixedColumnsReturn => {
  const [measured, setMeasured] = useState<MeasuredState | null>(null);

  // Calculate offsets and totals from widths state (fallback when no DOM ref)
  const calculated = useMemo(() => {
    const offsets: FixedColumnOffsets = {};
    let leftOffset = 0;
    let rightOffset = 0;

    // Calculate left offsets (iterate from left to right)
    columns.forEach((col) => {
      if (col.fixed === 'left') {
        offsets[col.key] = leftOffset;
        const colWidth = typeof widths[col.key] === 'number' ? (widths[col.key] as number) : DEFAULT_COLUMN_WIDTH;

        leftOffset += colWidth;
      }
    });

    // Calculate right offsets (iterate from right to left)
    [...columns].reverse().forEach((col) => {
      if (col.fixed === 'right') {
        offsets[col.key] = rightOffset;
        const colWidth = typeof widths[col.key] === 'number' ? (widths[col.key] as number) : DEFAULT_COLUMN_WIDTH;

        rightOffset += colWidth;
      }
    });

    return {
      offsets,
      totalLeft: leftOffset,
      totalRight: rightOffset
    };
  }, [columns, widths]);

  // Measure actual DOM widths and calculate offsets (more accurate)
  const measureAndCalculate = useCallback(() => {
    const wrapper = tableWrapperRef?.current;

    if (!wrapper) {
      return;
    }

    const offsets: FixedColumnOffsets = {};
    let totalLeft = 0;
    let totalRight = 0;

    // Measure left-fixed columns and calculate cumulative offsets
    const leftCells = wrapper.querySelectorAll('tbody tr:first-child .zen-table-cell-fix-left');

    leftCells.forEach((cell) => {
      const cellElement = cell as HTMLElement;
      const colKey = cellElement.dataset.testid?.replace('col-', '') || '';

      if (colKey) {
        offsets[colKey] = totalLeft;
      }
      totalLeft += cellElement.offsetWidth;
    });

    // Measure right-fixed columns and calculate cumulative offsets (from right to left)
    const rightCells = Array.from(wrapper.querySelectorAll('tbody tr:first-child .zen-table-cell-fix-right'));

    // Reverse to process from rightmost to leftmost
    rightCells.reverse().forEach((cell) => {
      const cellElement = cell as HTMLElement;
      const colKey = cellElement.dataset.testid?.replace('col-', '') || '';

      if (colKey) {
        offsets[colKey] = totalRight;
      }
      totalRight += cellElement.offsetWidth;
    });

    setMeasured({ offsets, totalLeft, totalRight });
  }, [tableWrapperRef]);

  // Initial measurement + ResizeObserver for external resizes
  useEffect(() => {
    if (!tableWrapperRef?.current) {
      return;
    }

    const wrapper = tableWrapperRef.current;

    measureAndCalculate();

    const resizeObserver = new ResizeObserver(measureAndCalculate);

    resizeObserver.observe(wrapper);

    return () => {
      resizeObserver.disconnect();
    };
  }, [tableWrapperRef, columns, measureAndCalculate]);

  // Re-measure when column widths change (e.g. after resize drag)
  // useLayoutEffect ensures measurement happens before paint, preventing shadow flicker
  useLayoutEffect(() => {
    measureAndCalculate();
  }, [widths, measureAndCalculate]);

  // Use measured values if available, otherwise fall back to calculated
  const result = measured || calculated;

  // Memoize the return value to maintain referential equality
  return useMemo(
    () => ({
      offsets: result.offsets,
      totalLeftFixedWidth: result.totalLeft,
      totalRightFixedWidth: result.totalRight
    }),
    [result]
  );
};
